// tailwind.config.js
const colors = require('tailwindcss/colors');
const plugin = require('tailwindcss/plugin');

// note: hub-* colors defined by triad color scheme method
// https://paletton.com/#uid=33q0u0k++UWrTZTF7+V+VxuZ+pD

module.exports = {
  content: [
    './pages/**/*.{js,jsx,ts,tsx}',
    './components/**/*.{js,jsx,ts,tsx}'
  ],
  theme: {
    backgroundImage: {
      'diagonal-stripes':
        'repeating-linear-gradient(45deg, white, white 1px, transparent 2px, transparent 8px)'
    },
    colors: {
      transparent: 'transparent',
      current: 'currentColor',
      black: colors.black,
      white: colors.white,
      gray: colors.gray,
      red: colors.red,
      green: colors.green,
      yellow: colors.amber,
      blue: colors.blue,
      pink: colors.pink,
      indigo: colors.indigo,
      // New Design Palette
      neutral: {
        DEFAULT: '#6F6F71',
        50: '#fff',
        100: '#F9F9FB',
        200: '#EFEFEF',
        300: '#DBDBDD',
        400: '#B7B7B9',
        500: '#98989A',
        600: '#6F6F71',
        700: '#5C5C5D',
        800: '#202323',
        900: '#000'
      },
      // Direct-response landing pages (FFH-1299): ink surfaces, slate copy,
      // hairline rules, muted decorative glyphs.
      landing: {
        ink: '#0b1b2b',
        slate: '#5b6b7b',
        hairline: '#e7edf2',
        surface: '#f5f6f8',
        muted: '#b6c4cf'
      },
      primary: {
        DEFAULT: '#009DE5',
        50: '#E8F6FD',
        100: '#D1EDFA',
        200: '#A3DCF6',
        300: '#75CAF1',
        400: '#47B8EC',
        500: '#1AA7E8',
        600: '#009DE5',
        700: '#0074A9',
        800: '#005880',
        900: '#003C57'
      },
      secondary: {
        DEFAULT: '#A51377',
        50: '#F8EBF4',
        100: '#F0D7E8',
        200: '#E1AFD1',
        300: '#D387BB',
        400: '#C45FA4',
        500: '#A81379',
        600: '#A51377',
        700: '#880F62',
        800: '#6C0C4D',
        900: '#4F0939'
      },
      success: {
        DEFAULT: '#088167',
        50: '#E7F5F2',
        100: '#CFECE6',
        200: '#9FD9CC',
        300: '#6FC5B3',
        400: '#3FB299',
        500: '#0A9D7D',
        600: '#088167',
        700: '#06634F',
        800: '#044436',
        900: '#02251E'
      },
      danger: {
        DEFAULT: '#CC0041',
        50: '#FFE6EE',
        100: '#FFCCDC',
        200: '#FF99B9',
        300: '#FF6697',
        400: '#FF3374',
        500: '#FF0051',
        600: '#CC0041',
        700: '#990031',
        800: '#660020',
        900: '#330010'
      },
      warning: {
        DEFAULT: '#B25E09',
        50: '#FFF8EB',
        100: '#FFF1D6',
        200: '#FEE2A9',
        300: '#FDCF72',
        400: '#FBBB3C',
        500: '#DB7712',
        600: '#B25E09',
        700: '#96530F',
        800: '#80460D',
        900: '#663B0F'
      },
      greenfriday: '#1BC669'
    },
    fontFamily: {
      sans: ['var(--font-poppins)', 'sans-serif'],
      'open-sans': ['var(--font-open-sans)', 'sans-serif'],
      // Semantic tokens for the CMS/funnel surfaces: default to the site
      // fonts, re-pointed to Manrope/Inter by .funnel-fonts (styles/globals.css).
      heading: ['var(--font-heading, var(--font-poppins))', 'sans-serif'],
      body: ['var(--font-body, var(--font-open-sans))', 'sans-serif'],
      mono: ['ui-monospace']
    },
    extend: {
      animation: {
        sparkle: 'sparkle 0.75s ease-in-out',
        slideInFromTop: 'slideInFromTop 0.5s ease-out',
        'dot-pulse': 'dotPulse 1.4s ease-in-out infinite',
        'silhouette-pulse': 'silhouettePulse 2.8s ease-in-out infinite',
        // Panel/backdrop pairs; the exit animations also let Radix delay
        // unmounting until the panel has left the screen.
        'fade-in': 'fadeIn 0.2s ease-out',
        'fade-out': 'fadeOut 0.15s ease-in',
        'slide-in-right': 'slideInFromRight 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        'slide-out-right': 'slideOutToRight 0.2s ease-in',
        'slide-in-bottom':
          'slideInFromBottom 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
        'slide-out-bottom': 'slideOutToBottom 0.2s ease-in',
        'sub-menu-fade-in': 'fadeIn 180ms ease-out',
        'sub-menu-slide-in': 'slideInFromBottom 180ms ease-out',
        'nav-panel-fade-in': 'fadeIn 0.35s ease-in',
        'spin-slow': 'spin 1.5s linear infinite',
        pulse: 'legacyPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
      },
      keyframes: (theme) => ({
        sparkle: {
          '0%': { opacity: 0, transform: 'scale(0.5)' },
          '50%': { opacity: 0.8, transform: 'scale(1.2)' },
          '100%': { opacity: 1, transform: 'scale(1)' }
        },
        slideInFromTop: {
          '0%': { transform: 'translateY(-100%)', opacity: 0 },
          '100%': { transform: 'translateY(0)', opacity: 1 }
        },
        fadeIn: {
          '0%': { opacity: 0 },
          '100%': { opacity: 1 }
        },
        fadeOut: {
          '0%': { opacity: 1 },
          '100%': { opacity: 0 }
        },
        slideInFromRight: {
          '0%': { transform: 'translateX(100%)' },
          '100%': { transform: 'translateX(0)' }
        },
        slideOutToRight: {
          '0%': { transform: 'translateX(0)' },
          '100%': { transform: 'translateX(100%)' }
        },
        slideInFromBottom: {
          '0%': { transform: 'translateY(100%)' },
          '100%': { transform: 'translateY(0)' }
        },
        slideOutToBottom: {
          '0%': { transform: 'translateY(0)' },
          '100%': { transform: 'translateY(100%)' }
        },
        fadeOutColor: {
          '0%': { backgroundColor: theme('colors.transparent') },
          '100%': { backgroundColor: theme('colors.black') }
        },
        dotPulse: {
          '0%, 80%, 100%': { opacity: 0.3, transform: 'scale(0.8)' },
          '40%': { opacity: 1, transform: 'scale(1)' }
        },
        silhouettePulse: {
          '0%, 100%': { opacity: 0 },
          '50%': { opacity: 1 }
        },
        // The removed SCSS football loader declared its own `pulse` keyframes
        // after Tailwind's, so every animate-pulse skeleton on the site has
        // always pulsed this way. The pulse animation above keeps that look.
        legacyPulse: {
          '0%': { opacity: 0.1 },
          '30%': { opacity: 0.8 },
          '100%': { opacity: 0.1 }
        }
      }),
      spacing: {
        128: '32rem',
        144: '36rem'
      },
      borderRadius: {
        '4xl': '2rem'
      },
      fontSize: {
        xxxs: '0.5rem',
        xxs: '0.625rem',
        '3xl': '1.75rem',
        '4xl': '2rem'
      },
      lineHeight: {
        xxxs: '0.5rem',
        xxs: '0.625rem',
        '3xl': '2.625rem',
        '4xl': '3rem'
      },
      height: {
        '69vh': '69vh',
        '95vh': '95vh'
      },
      boxShadow: {
        DEFAULT: '0px 2px 15px rgba(0, 0, 0, 0.05)',
        100: '0px 2px 15px rgba(0, 0, 0, 0.05)',
        300: '0px 2px 12px rgba(0, 0, 0, 0.1)',
        500: '0px 2px 25px rgba(0, 0, 0, 0.15)'
      },
      maxHeight: {
        '40vh': '40vh',
        '50vh': '50vh',
        '70vh': '70vh',
        '80vh': '80vh',
        '90vh': '90vh',
        '95vh': '95vh'
      },
      maxWidth: {
        xxs: '15rem',
        '100vw': '100vw'
      },
      minHeight: {
        20: '20vw',
        '26vh': '26vh',
        '36vh': '36vh'
      },
      minWidth: {
        40: '40vw',
        50: '50vw',
        60: '60vw',
        70: '70vw',
        80: '80vw',
        90: '90vw',
        95: '95vw'
      },
      screens: {
        // xxs: '340px',
        xs: '420px',
        mdl: '981px',
        xxl: '1535px'
      },
      transitionProperty: {
        height: 'height'
      },
      width: {
        112: '28rem',
        '65vw': '65vw',
        '75vw': '75vw',
        '90vw': '90vw',
        '95%': '95%'
      }
    }
  },
  plugins: [
    require('@tailwindcss/forms'),
    require('@tailwindcss/typography'),
    plugin(function ({ addUtilities }) {
      addUtilities({
        '.scrollbar-hide': {
          /* IE and Edge */
          '-ms-overflow-style': 'none',
          /* Firefox */
          'scrollbar-width': 'none',
          /* Safari and Chrome */
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }
      });
    })
  ]
};
